import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="idle-bird" on the landing page hero's bird mark. Shortly after the
// page loads the bird bends down and pecks at the ground once. After that, while the page sits
// untouched, it now and then does one small move. It never moves while the user is doing
// something, so it reads as a small surprise rather than a distraction. Users who ask for reduced
// motion never see it.
const INTRO_MS = 2_500
const INTRO_MOVE = "bird-feed"
const IDLE_MS = 15_000
const RETRY_MIN_MS = 20_000
const RETRY_MAX_MS = 45_000
const CHANCE = 0.6
const MOVES = [ "bird-feed", "bird-tilt", "bird-hop", "bird-peck", "bird-blink" ]
const ACTIVITY_EVENTS = [ "pointermove", "pointerdown", "keydown", "scroll", "touchstart", "wheel" ]

export default class extends Controller {
  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.reset = this.reset.bind(this)
    ACTIVITY_EVENTS.forEach(name => window.addEventListener(name, this.reset, { passive: true }))
    document.addEventListener("visibilitychange", this.reset)
    this.element.addEventListener("animationend", () => this.element.classList.remove(...MOVES))
    this.introTimer = setTimeout(() => this.play(INTRO_MOVE), INTRO_MS)
    this.reset()
  }

  disconnect() {
    clearTimeout(this.timer)
    clearTimeout(this.introTimer)
    if (!this.reset) return

    ACTIVITY_EVENTS.forEach(name => window.removeEventListener(name, this.reset))
    document.removeEventListener("visibilitychange", this.reset)
  }

  // Any activity starts the idle wait over. pointermove fires often, so this stays cheap.
  reset() {
    this.schedule(IDLE_MS)
  }

  schedule(delay) {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.maybeMove(), delay)
  }

  // The first idle moment always moves, so a visitor who stops to read sees it once. After that
  // it only sometimes does.
  maybeMove() {
    if (document.visibilityState === "visible" && (!this.hasMoved || Math.random() < CHANCE)) {
      // The blink is easy to miss, so it's never the first move.
      const pool = this.hasMoved ? MOVES : MOVES.filter(move => move !== "bird-blink")
      this.play(pool[Math.floor(Math.random() * pool.length)])
      this.hasMoved = true
    }
    this.schedule(RETRY_MIN_MS + Math.random() * (RETRY_MAX_MS - RETRY_MIN_MS))
  }

  play(move) {
    if (document.visibilityState !== "visible") return

    this.element.classList.remove(...MOVES)
    // Forcing a layout restarts the animation if the same move comes up twice in a row.
    void this.element.getBoundingClientRect()
    this.element.classList.add(move)
  }
}
